// step 2 of the sponsor pledge flow

import React from 'react'
import Icon from '../../../../components/icon'
import { formatAmount } from '../../../../lib/numbers'
import { round2 } from '../../../../lib/sponsorPledgeFlowMath'

export default function Step2Allocation({
  styles,
  t,
  commitmentNum,
  sliderPct,
  onSlider,
  allocWarn,
  vpfAmount,
  mpfAmount,
  onVpfInput,
  onMpfInput,
  vpfInputDraft,
  mpfInputDraft,
  setIsEditingVpf,
  setIsEditingMpf,
  setVpfInputDraft,
  setMpfInputDraft,
  volunteerCount,
  estimatedVolunteerHours,
  vpfVolunteerShare,
  vpfOrgShare,
  opsAmount,
  setOpsAmount,
  setStep,
}) {
  return (
    <>
      <h1 className={styles.stepHeading}>
        {t('sponsorPledgeFlow.step2.heading')}
      </h1>
      <p className={styles.stepSubheading}>
        {t('sponsorPledgeFlow.step2.subheading')}
      </p>

      <div className={styles.allocCard}>
        <div className={styles.allocCardHeader}>
          <div>
            <div className={styles.allocCardSublabel}>
              {t('sponsorPledgeFlow.step2.cardPledgeLabel')}
            </div>
            <div className={styles.allocCardPledge}>
              {formatAmount(commitmentNum, true)}
            </div>
          </div>
          <div>
            <div
              className={styles.allocCardSublabel}
              style={{ textAlign: 'right' }}
            >
              {t('sponsorPledgeFlow.step2.cardImpactLabel')}
            </div>
            <div className={styles.allocImpact}>
              {formatAmount(commitmentNum, true)}
            </div>
            <div className={styles.allocImpactSub}>
              {t('sponsorPledgeFlow.step2.cardImpactSub')}
            </div>
          </div>
        </div>

        <div className={styles.allocSplitLabel}>
          {t('sponsorPledgeFlow.step2.splitLabel')}
        </div>
        <div className={styles.allocSliderWrap}>
          <div className={styles.allocBarTrack}>
            <div
              className={styles.allocBarVpf}
              style={{ width: `${sliderPct}%` }}
            />
            <div
              className={styles.allocBarMpf}
              style={{ width: `${100 - sliderPct}%` }}
            />
          </div>
          <input
            type="range"
            className={styles.crSlider}
            min={0}
            max={100}
            value={sliderPct}
            onChange={onSlider}
          />
        </div>
        <div className={styles.splitLabels}>
          <span style={{ color: 'var(--purple)', fontWeight: 600 }}>
            {t('sponsorPledgeFlow.step2.vpfPct', { pct: sliderPct })}
          </span>
          <span style={{ color: 'var(--pink)', fontWeight: 600 }}>
            {t('sponsorPledgeFlow.step2.mpfPct', { pct: 100 - sliderPct })}
          </span>
        </div>

        <div
          className={[
            styles.allocWarning,
            allocWarn ? styles.allocWarningShow : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <Icon name="info-circle" />
          <span>
            {t('sponsorPledgeFlow.step2.allocWarning', {
              total: formatAmount(commitmentNum, true),
              remaining: formatAmount(
                round2(commitmentNum - vpfAmount - mpfAmount),
                true,
              ),
            })}
          </span>
        </div>

        <div className={styles.allocCols}>
          <div className={styles.allocCol}>
            <div className={styles.allocColHead}>
              <div className={styles.allocBadgeRow}>
                <div
                  className={`${styles.allocColBadge} ${styles.allocColBadgeVpf}`}
                >
                  VPF
                </div>
                <div className={styles.allocInfoWrap}>
                  <button
                    type="button"
                    className={styles.allocInfoBtn}
                    aria-label={t('sponsorPledgeFlow.step2.vpfTooltip')}
                  >
                    <Icon name="info-circle" />
                  </button>
                  <div className={styles.allocInfoTooltip}>
                    {t('sponsorPledgeFlow.step2.vpfTooltip')}
                  </div>
                </div>
              </div>
              <div className={styles.allocColAmountWrap}>
                <span style={{ fontWeight: 600, color: 'var(--light)' }}>
                  $
                </span>
                <input
                  className={styles.allocColAmount}
                  type="text"
                  inputMode="decimal"
                  value={vpfInputDraft}
                  onChange={onVpfInput}
                  onFocus={() => setIsEditingVpf(true)}
                  onBlur={() => {
                    setIsEditingVpf(false)
                    setVpfInputDraft(vpfAmount.toFixed(2))
                  }}
                />
              </div>
              <div style={{ fontSize: 11, color: 'var(--light)' }}>
                {t('sponsorPledgeFlow.step2.volHourLine', {
                  vol: volunteerCount,
                  hrs: estimatedVolunteerHours ?? '—',
                })}
              </div>
            </div>
            <div className={styles.allocColBody}>
              <div className={styles.allocColStat}>
                {t('sponsorPledgeFlow.step2.vpfVolShare', {
                  amount: formatAmount(vpfVolunteerShare, true),
                })}
              </div>
              <div className={styles.allocColStat} style={{ marginTop: 8 }}>
                {t('sponsorPledgeFlow.step2.vpfOrgShare', {
                  amount: formatAmount(vpfOrgShare, true),
                })}
              </div>
            </div>
          </div>
          <div className={styles.allocCol}>
            <div className={styles.allocColHead}>
              <div className={styles.allocBadgeRow}>
                <div
                  className={`${styles.allocColBadge} ${styles.allocColBadgeMpf}`}
                >
                  MPF
                </div>
                <div className={styles.allocInfoWrap}>
                  <button
                    type="button"
                    className={styles.allocInfoBtn}
                    aria-label={t('sponsorPledgeFlow.step2.mpfTooltip')}
                  >
                    <Icon name="info-circle" />
                  </button>
                  <div className={styles.allocInfoTooltip}>
                    {t('sponsorPledgeFlow.step2.mpfTooltip')}
                  </div>
                </div>
              </div>
              <div className={styles.allocColAmountWrap}>
                <span style={{ fontWeight: 600, color: 'var(--light)' }}>
                  $
                </span>
                <input
                  className={styles.allocColAmount}
                  type="text"
                  inputMode="decimal"
                  value={mpfInputDraft}
                  onChange={onMpfInput}
                  onFocus={() => setIsEditingMpf(true)}
                  onBlur={() => {
                    setIsEditingMpf(false)
                    setMpfInputDraft(mpfAmount.toFixed(2))
                  }}
                />
              </div>
              <div style={{ fontSize: 11, color: 'var(--light)' }}>
                {t('sponsorPledgeFlow.step2.mpfMaxMatch')}
              </div>
            </div>
            <div className={styles.allocColBody}>
              <div className={styles.allocColStat}>
                {t('sponsorPledgeFlow.step2.mpfDonors', {
                  amount: formatAmount(mpfAmount, true),
                })}
              </div>
              <div className={styles.allocColStat} style={{ marginTop: 8 }}>
                {t('sponsorPledgeFlow.step2.mpfMatch', {
                  amount: formatAmount(mpfAmount, true),
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.opsSection}>
        <div className={styles.opsHeading}>
          {t('sponsorPledgeFlow.step2.opsTitle')}
          <span className={styles.optionalTag}>
            {t('sponsorPledgeFlow.optional')}
          </span>
        </div>
        <p className={styles.opsDesc}>{t('sponsorPledgeFlow.step2.opsDesc')}</p>
        <div className={styles.opsInputWrap}>
          <span className={styles.opsCurrency}>$</span>
          <input
            className={styles.opsInput}
            type="number"
            min={0}
            step={25}
            value={opsAmount}
            onChange={e => setOpsAmount(e.target.value)}
            placeholder="0"
          />
        </div>
      </div>

      <div className={styles.stepNav}>
        <button
          type="button"
          className={styles.btnBack}
          onClick={() => setStep(1)}
        >
          ‹ {t('buttons.back')}
        </button>
        <button
          type="button"
          className={styles.btnPrimary}
          disabled={allocWarn || !commitmentNum}
          onClick={() => setStep(3)}
        >
          {t('buttons.continue')}
          <Icon name="chevron-right" />
        </button>
      </div>
    </>
  )
}
